import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { deleteSchoolDirect } from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import type { School } from '@timetable/types';
import { ConfirmationDialog } from '../shared/ConfirmationDialog';

interface SchoolListProps {
  schools?: School[];
  isLoading?: boolean;
  onEdit?: (school: School) => void;
}

export function SchoolList({ schools, isLoading, onEdit }: SchoolListProps) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<School | null>(null);
  const [copiedSchoolId, setCopiedSchoolId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (schoolId: string) => deleteSchoolDirect(schoolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.all });
      setDeleteDialogOpen(false);
      setSchoolToDelete(null);
    },
  });

  const handleDeleteClick = (school: School) => {
    setSchoolToDelete(school);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (schoolToDelete) {
      deleteMutation.mutate(schoolToDelete.school_id);
    }
  };

  const handleCopySchoolId = (schoolId: string) => {
    navigator.clipboard.writeText(schoolId);
    setCopiedSchoolId(schoolId);
    setTimeout(() => setCopiedSchoolId(null), 2000);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Loading schools...</Typography>
      </Box>
    );
  }

  if (!schools || schools.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No schools registered yet. Click "Register School" to add one.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>School Name</TableCell>
              <TableCell>School ID</TableCell>
              <TableCell>Contact Person</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schools.map((school) => (
              <TableRow key={school.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {school.school_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {school.address}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight="bold"
                    >
                      {school.school_id}
                    </Typography>
                    <Tooltip
                      title={
                        copiedSchoolId === school.school_id
                          ? 'Copied!'
                          : 'Copy School ID'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopySchoolId(school.school_id)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{school.contact_person}</TableCell>
                <TableCell>{school.email}</TableCell>
                <TableCell>{school.phone}</TableCell>
                <TableCell>
                  <Chip
                    label="Active"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {onEdit && (
                      <Tooltip title="Edit school">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(school)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete school">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(school)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete School"
        message={`Are you sure you want to delete ${schoolToDelete?.school_name}? This will permanently delete all data associated with this school, including users, timetables, and settings.`}
        confirmText="Delete School"
        isDestructive
        isLoading={deleteMutation.isPending}
        error={deleteMutation.error as Error | null}
        warningMessage="This action cannot be undone!"
      />
    </>
  );
}
